import { isNonEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { FormattedMessage } from 'components/shared/Intl';
import type { IntlKey } from 'locales';
import type { SimpleGridFieldData, SimpleGridFieldMetadata } from 'models/forms/fields';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { mediaQueryDown } from 'utils/mediaQueries';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';
import { GridHeaders } from '../Grid/GridHeaders';
import type { TableRowData } from './TableGridColumns';
import { getDefaultValueForNewTableRow, TableGridColumns } from './TableGridColumns';
import { TableGridRow } from './TableGridRow';
import { Warning } from './Warning';

const FitContentDiv = styled.div`
  ${mediaQueryDown('md')`width: fit-content;`}
`;

export const SimpleGridFieldWithHeader: FC<ProductNodeBuilderProps> = ({
  node: { id, labelSize },
  workspaceType,
  tabIndex,
}) => {
  const [data, setData] = useData<SimpleGridFieldData>(id);
  const [isInsertionMode, setInsertionMode] = useState<boolean>(false);
  const hasError = useIsFieldInvalid(id);
  const metadata = useFieldMetadata<SimpleGridFieldMetadata>(id)!;
  const columnMetadata = metadata.columnFieldDefinitions!;

  const onAddRowClicked = useCallback(() => setInsertionMode(true), []);

  const rows = useMemo<ReadonlyArray<TableRowData>>(() => {
    if (isInsertionMode) {
      // empty row with null values
      const newEmptyRow = columnMetadata.reduce<TableRowData>((acc, colMeta) => {
        acc[colMeta.id] = getDefaultValueForNewTableRow(colMeta);
        return acc;
      }, {});
      return [...(data || []), newEmptyRow];
    } else {
      return data || [];
    }
  }, [isInsertionMode, columnMetadata, data]);

  const onRowUpdated = useCallback(
    (rowIndex: number, value: TableRowData) =>
      setData([...rows.slice(0, rowIndex), value, ...rows.slice(rowIndex + 1)]),
    [rows, setData],
  );

  const onRowRemoved = useCallback(
    (rowIndex: number) => {
      const updatedData = [...rows.slice(0, rowIndex), ...rows.slice(rowIndex + 1)];
      setData(updatedData);
    },
    [rows, setData],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Rerun this effect when data changes
  useEffect(() => setInsertionMode(false), [data]);

  const headers = useMemo(() => {
    const result = columnMetadata.map(h => ({
      id: h.id,
      translationKey: h.translationKey,
    }));
    return result.concat({
      id: 'RemoveRow',
      translationKey: 'RemoveRow',
    });
  }, [columnMetadata]);

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${metadata.translationKey}` as any}
      labelSize={labelSize}
      workspaceType={workspaceType}
    >
      <div className={classNames({ 'border border-warning': hasError })}>
        <div className="overflow-auto">
          <FitContentDiv data-e2e={id}>
            <table
              className="table table-form table-bordered table-thead-bordered m-0"
              data-e2e={'editable-grid-' + id}
            >
              <GridHeaders id={id} headers={headers} hasRowIndexColumn={false} />
              {rows.map((row, index) => (
                <TableGridRow key={index} fieldId={id} isLoading={false} index={index} removeRow={onRowRemoved}>
                  <TableGridColumns
                    fieldId={id}
                    rowIndex={index}
                    row={row}
                    setRowValue={onRowUpdated}
                    metadata={columnMetadata}
                  />
                </TableGridRow>
              ))}
            </table>
          </FitContentDiv>
        </div>
      </div>
      <div className="row ms-0">
        <button
          type="button"
          id={id + '-add-line'}
          className="btn btn-flat-primary btn-icon-start text-start"
          disabled={isInsertionMode}
          onClick={onAddRowClicked}
          data-e2e={id + '-item-add'}
          tabIndex={tabIndex}
        >
          <i className="icon">add</i>
          <FormattedMessage id="form.Add" />
        </button>
        {isNonEmpty(data) && <Warning warningId={metadata.infoTranslationKey as IntlKey} />}
      </div>
    </BlockOrientationFormGroup>
  );
};

SimpleGridFieldWithHeader.displayName = 'SimpleGridFieldWithHeader';
